import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { useEffect } from 'react';
import { fetchUser } from "./store/common/authSlice";
import {
  fetchCurrentEmployee,
  resetAuthState,
} from "./store/allocation/allocationAuthSlice";
import BrandThemeSync from "./components/utilities/BrandThemeSync";
import PunchInReminder from "./components/hr/PunchInReminder";

// static routes
import Home from "./pages/staticPages/Home";
import About from "./pages/staticPages/AboutUs";
import ContactUs from "./pages/staticPages/ContactUs";
import FAQ from "./pages/staticPages/FAQ";
import Services from "./pages/staticPages/Services";
import PrivacyPolicy from "./pages/staticPages/PrivacyPolicy";
import PageNotFound from "./pages/staticPages/PageNotFound";
import TermsConditions from "./pages/staticPages/TermsConditions";
import Careers from "./pages/staticPages/Careers";

// Layouts
import StaticView from "./components/staticView/layout";
import CasesDashboardLayout from "./components/cases-dashboard/layout";
import SmileDashboardLayout from "./components/smile-dashboard/layout";
import AllocationPageLayout from "./components/allocation-dashboard/layout";
import EmployeeDashboardLayout from "./components/employee-dashboard/layout";
import HrLegacyRedirect, { HrAdminOnly } from "./pages/hr/IndexRedirect";
import AdminCaseLayout from "./components/admin/layout";
import StlViewerPage from "./pages/admin/StlViewerPage";
import OcclusionContactAnalysis from "./pages/occlusion/OcclusionContactAnalysis";

// check auth
import CheckAuth from "./components/auth/checkAuth";
import SmileCheckAuth from "./components/auth/smileCheckAuth";
import LoginLayout from "./components/auth/loginLayout";
import AuthRedirector from "./components/auth/authRedirector";

// Cases Dashboard
import Profile from "./pages/common/Profile";
import LoginAs from "./pages/superAdmin/impersonation";
import Requests from "./pages/admin/Requests";
import Roles from "./pages/superAdmin/Roles";
import CaseDetailsForm from "./components/uploader/caseDetailsForm";
import AdminCases from "./pages/admin/cases";
import Redesigns from "./pages/admin/Redesigns";
import MyCase from "./pages/user/myCase";
import UrisPackageManager from "./pages/admin/UrisPackageManager";
import UrisManualTokens from "./pages/admin/UrisManualTokens";
import HoldCases from "./pages/allocation/HoldCases";
import ReadyToUpload from "./pages/admin/ReadyToUpload";
import LabDetails from "./pages/admin/labDetails";
import CaseOk from "./pages/admin/caseOk";
import DesignReady from "./pages/admin/designReady";
import CaseUnits from "./pages/admin/caseUnits";
import CaseFzUnits from "./pages/admin/caseFzUnits";
import UnitsLedger from "./pages/admin/UnitsLedger";
import MistakeTracker from "./pages/admin/MistakeTracker";
import GenerateInvoices from "./pages/superAdmin/generateInvoices";
import LabBillingPricing from "./pages/superAdmin/LabBillingPricing";
import LabBillingPricingDetails from "./pages/superAdmin/LabBillingPricingDetails";
import MarkedUnits from "./pages/admin/MarkedUnits";
import RecentlyAssignedUnits from "./pages/admin/RecentlyAssignedUnits";
import AdminInvoices from "./pages/superAdmin/adminInvoices";
import MyInvoices from "./pages/user/myInvoices";
import Testimonials from "./pages/superAdmin/Testimonials";
import DataExports from "./pages/superAdmin/DataExports";
import CaseLifecycleReport from "./pages/superAdmin/CaseLifecycleReport";
import LocalDownloaderStatus from "./pages/admin/LocalDownloaderStatus";
// some extra cases portal routes
import CaseFileUpload from "./components/uploader/caseFileUpload";
import TestimonialForm from "./pages/staticPages/TestimonialForm";
import LabPrefrences from "./pages/admin/LabPrefrences";

// Allocation Dashboard
import AllocationShifts from "./pages/allocation/Shifts";
import Unallocated from "./pages/allocation/UnAllocatedCases";
import AllocationLogs from "./pages/allocation/AllocationLogs";
import AllocationUpdates from "./pages/allocation/AllocationUpdates";
import AllocationShiftHistory from "./pages/allocation/ShiftHistory";

// Employee Dashboard
import EmployeeOverview from "./pages/employee/Overview";
import EmployeeCases from "./pages/employee/Cases";
import EmployeeDashboardIndex from "./pages/employee/IndexRedirect";
import EmployeeReviews from "./pages/employee/Reviews";
import EmployeeAttendance from "./pages/employee/AttendanceReport";

// HR & People
import HrIndexRedirect from "./pages/hr/IndexRedirect";
import HrDirectory from "./pages/hr/Directory";
import HrLeaves from "./pages/hr/Leaves";
import LeaveBalanceLogs from "./pages/hr/LeaveBalanceLogs";
import HrOrganization from "./pages/hr/Organization";

// Smile Design
import SmileGeneration from "./pages/smileDesign/SmileGeneration";
import PromptManager from "./pages/smileDesign/PromptManager";
import AllSmiles from "./pages/smileDesign/AllSmiles";
import ListCreditPackages from "./pages/smileDesign/ListCreditPackages";
import ManualCredits from "./pages/smileDesign/ManualCredits";
import CreditAuditLogs from "./pages/smileDesign/CreditAuditLogs";
import MySmiles from "./pages/smileDesign/MySmiles";
import PackageManager from "./pages/smileDesign/PackageManager";
import PaymentManager from "./pages/smileDesign/PaymentManager";
import PaymentSuccess from "./pages/smileDesign/PaymentSuccess";
import PaymentCancelled from "./pages/smileDesign/PaymentCancelled";
import MyOrders from "./pages/smileDesign/MyOrders";
import CreditUsageHistory from "./pages/smileDesign/CreditUsageHistory";
import RefundManager from "./pages/smileDesign/RefundManager";
import ClientManagement from "./pages/smileDesign/ClientManagement";
import { CREDIT_SERVICE } from "./constants/creditService";
import PhotoRecolourStudio from "./pages/photoRecolour/PhotoRecolourStudio";
import SurgerySlotsAdmin from "./pages/admin/SurgerySlotsAdmin";
import BookSurgery from "./pages/user/BookSurgery";


// RBAC Roles
const allRoles = [
  "Super Admin",
  "Side Admin",
  "Admin Read",
  "Admin",
  "Operations",
  "Production",
  "User",
];

const dashboardViewRoles = [
  "Super Admin",
  "Side Admin",
  "Admin Read",
  "Admin",
  "Operations",
  "Production",
];

const redesignViewRoles = [
  "Super Admin",
  "Admin Read",
  "Admin",
  "Operations",
  "Production",
];
const uploadCaseRoles = ["Super Admin", "Admin", "Side Admin", "Admin Read", "Operations", "User"];
const labAccessRoles = [
  "Super Admin",
  "Side Admin",
  "Admin",
  "Admin Read",
  "Operations",
  "Production",
];
const allocationPortalAccessRoles = [
  "Super Admin",
  "Admin",
  "Operations",
  "Production",
  "Captain",
  "Assistant Captain",
];
const allocationPortalEmployeeRoles = ["Admin"];
const allocationPortalAllocateRoles = ["Admin", "Operations", "Captain", "Assistant Captain"];

const invoiceAccessRoles = ["Super Admin", "Admin Read"];
const bulkCaseRoles = ["Super Admin", "Admin", "Operations"];
const superAdminReadAdminRoles = ["Super Admin", "Admin Read"];


const smilePortalClientRoles = ["Super Admin", "Admin Read", "Side Admin", "User"];
const smilePortalAdminRoles = ["Super Admin", "Admin Read"];
const superAdminRoles = ["Super Admin"];
const labPricingRoles = ["Super Admin", "Operations"];
const caseReportRoles = ["Super Admin", "Operations"];
const markedUnitsRoles = ["Super Admin", "Operations"];
const mistakesAccessRoles = ["Super Admin", "Admin", "Operations"];
const employeeDashboardRoles = ["Production", "Operations", "Super Admin"];
const surgerySlotsAdminRoles = ["Super Admin", "Operations", "Production"];

const needsEmployeeSession = (role?: string | null) =>
  !!role && role.toLowerCase() !== "user";

const App: React.FC = () => {

  const dispatch = useAppDispatch();
  const location = useLocation();
  const { user, isAuth } = useAppSelector((state) => state.auth);
  const loadEmployeeSession = isAuth && needsEmployeeSession(user?.role);

  useEffect(() => {
    dispatch(fetchUser());
  }, [dispatch]);

  useEffect(() => {
    if (!loadEmployeeSession) return;
    dispatch(fetchCurrentEmployee());
  }, [dispatch, loadEmployeeSession]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "allocation-logout") {
        dispatch(resetAuthState());
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [dispatch]);

  // Soft session check when the tab becomes visible again — throttled so it
  // does not keep reloading employee dashboard pages on every alt-tab.
  useEffect(() => {
    if (!loadEmployeeSession) return;
    let lastFetch = 0;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastFetch < 60_000) return;
      lastFetch = now;
      dispatch(fetchCurrentEmployee());
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [dispatch, loadEmployeeSession]);


  return (
    <div>
      <BrandThemeSync />
      <PunchInReminder />
      <Routes>

        {/* Static Routes */}
        <Route path="/" element={<StaticView />}>
          <Route index element={<Home />} />
          <Route path="about" element={<About />} />
          <Route path="contact" element={<ContactUs />} />
          <Route path="careers" element={<Careers />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="services" element={<Services />} />
          <Route path="add/testimonial" element={<TestimonialForm />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="terms-conditions" element={<TermsConditions />} />
        </Route>

        <Route path="/dashboard" element={<LoginLayout />} />

        {/* Full-page STL viewer (new tab) */}
        <Route element={<CheckAuth allowedRoles={dashboardViewRoles} />}>
          <Route
            path="/dashboard/stl-viewer/:fileId"
            element={<StlViewerPage />}
          />
        </Route>

        {/* Occlusion contact tool — Super Admin only */}
        <Route element={<CheckAuth allowedRoles={superAdminRoles} />}>
          <Route
            path="/dashboard/occlusion-contact"
            element={<OcclusionContactAnalysis />}
          />
        </Route>

        {/* Cases Portal */}
        <Route path="/dashboard" element={<CasesDashboardLayout />}>
          <Route element={<CheckAuth allowedRoles={allRoles} />}>
            <Route path="dashboard" element={<AuthRedirector />} />
            <Route path="profile" element={<Profile />} />
            <Route path="my-invoices" element={<MyInvoices />} />
            <Route path="photo-recolour" element={<PhotoRecolourStudio />} />
            <Route
              path="photo-recolour/packages"
              element={
                <ListCreditPackages service={CREDIT_SERVICE.PHOTO_RECOLOUR} />
              }
            />
            <Route
              path="photo-recolour/payment-success"
              element={<PaymentSuccess />}
            />
            <Route
              path="photo-recolour/payment-cancelled"
              element={<PaymentCancelled />}
            />
            <Route
              path="photo-recolour/order-history"
              element={<MyOrders service={CREDIT_SERVICE.PHOTO_RECOLOUR} />}
            />
          </Route>

          <Route element={<CheckAuth allowedRoles={["User"]} />}>
            <Route path="my-cases" element={<MyCase />} />
            <Route path="book-surgery" element={<BookSurgery />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={dashboardViewRoles} />}>
            <Route path="admin/my-cases" element={<MyCase />} />
            <Route path="admin/cases" element={<AdminCases />} />
            <Route path="admin/cases/:case_id" element={<AdminCaseLayout />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={surgerySlotsAdminRoles} />}>
            <Route path="admin/surgery-slots" element={<SurgerySlotsAdmin />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={redesignViewRoles} />}>
            <Route path="admin/redesigns" element={<Redesigns />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={uploadCaseRoles} />}>
            <Route path="upload" element={<CaseDetailsForm />} />
            <Route path="upload/:case_id" element={<CaseFileUpload />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={labAccessRoles} />}>
            <Route path="admin/labdetails" element={<LabDetails />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={["Side Admin"]} />}>
            <Route path="admin/uris-packages" element={<UrisPackageManager />} />
            <Route path="admin/uris-tokens" element={<UrisManualTokens />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={superAdminReadAdminRoles} />}>
            <Route path="admin/all/testimonials" element={<Testimonials />} />
          </Route>


          <Route element={<CheckAuth allowedRoles={superAdminRoles} />}>
            <Route
              path="admin/generate/invoices"
              element={<GenerateInvoices />}
            />
            <Route path="admin/impersonation" element={<LoginAs />} />
            <Route path="admin/roles" element={<Roles />} />
            <Route
              path="admin/local-downloader"
              element={<LocalDownloaderStatus />}
            />
            <Route
              path="admin/photo-recolour-packages"
              element={
                <PackageManager service={CREDIT_SERVICE.PHOTO_RECOLOUR} />
              }
            />
          </Route>

          <Route element={<CheckAuth allowedRoles={caseReportRoles} />}>
            <Route
              path="admin/data/case-report"
              element={<CaseLifecycleReport />}
            />
          </Route>

          <Route element={<CheckAuth allowedRoles={labPricingRoles} />}>
            <Route
              path="admin/lab-pricing"
              element={<LabBillingPricing />}
            />
            <Route
              path="admin/lab-pricing/details"
              element={<LabBillingPricingDetails />}
            />
          </Route>

          {/* Mistakes: Super Admin, or Admin employee (CRM/Admin portal) */}
          <Route element={<CheckAuth allowedRoles={mistakesAccessRoles} />}>
            <Route path="admin/mistakes" element={<MistakeTracker />} />
          </Route>

          {/* Marked Units: Super Admin, or CRM portal with an Admin employee (CRM employees are blocked in-page) */}
          <Route element={<CheckAuth allowedRoles={markedUnitsRoles} />}>
            <Route path="admin/marked-units" element={<MarkedUnits />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={invoiceAccessRoles} />}>
            <Route path="admin/requests" element={<Requests />} />
            <Route path="admin/invoices" element={<AdminInvoices />} />
          </Route>

          <Route
            element={
              <CheckAuth
                allowedRoles={allocationPortalAccessRoles}
                allocationRoles={allocationPortalEmployeeRoles}
              />
            }
          >
            <Route path="employees" element={<Navigate to="/dashboard/employee/directory" replace />} />
            <Route path="shifts" element={<Navigate to="/dashboard/employee/shifts" replace />} />
          </Route>

          <Route
            element={
              <CheckAuth
                allowedRoles={allocationPortalAccessRoles}
                allocationRoles={allocationPortalAllocateRoles}
              />
            }
          >
            <Route path="admin/ready-to-upload" element={<ReadyToUpload />} />
            <Route path="hold-cases" element={<HoldCases />} />
          </Route>

          <Route element={<CheckAuth allowedRoles={bulkCaseRoles} />}>
            <Route path="admin/caseok" element={<CaseOk />} />
            <Route path="admin/designready" element={<DesignReady />} />
            <Route
              path="admin/labpreference/:token"
              element={<LabPrefrences />}
            />
            <Route path="admin/units" element={<CaseUnits />} />
            <Route path="admin/units/fz" element={<CaseFzUnits />} />
            <Route path="admin/units/ledger" element={<UnitsLedger />} />
            <Route
              path="admin/units/recent"
              element={<RecentlyAssignedUnits />}
            />
            <Route path="admin/data/export" element={<DataExports />} />
          </Route>

        </Route>


        {/* Allocation Portal */}
        <Route
          path="/dashboard/allocation"
          element={<AllocationPageLayout />}
        >
          <Route
            element={
              <CheckAuth
                allowedRoles={dashboardViewRoles}
                allocationRoles={dashboardViewRoles}
              />
            }
          >
            <Route path="Unallocated" element={<Unallocated />} />
            <Route path="Updates" element={<AllocationUpdates />} />
            <Route path="logs" element={<AllocationLogs />} />
            <Route path="history" element={<AllocationShiftHistory />} />
          </Route>
        </Route>

        {/* Employee Dashboard */}
        <Route path="/dashboard/employee" element={<EmployeeDashboardLayout />}>
          <Route element={<CheckAuth allowedRoles={employeeDashboardRoles} />}>
            <Route index element={<EmployeeDashboardIndex />} />
            <Route path="overview" element={<EmployeeOverview />} />
            <Route path="cases" element={<EmployeeCases />} />
            <Route path="reviews" element={<EmployeeReviews />} />
            <Route path="my-attendance" element={<EmployeeAttendance />} />
            <Route path="leaves" element={<HrLeaves />} />
            <Route path="leave-balance-logs" element={<LeaveBalanceLogs />} />
            <Route path="team" element={<Navigate to="/dashboard/employee/overview" replace />} />
            <Route element={<HrAdminOnly />}>
              <Route path="people" element={<Navigate to="/dashboard/employee/directory" replace />} />
              <Route path="directory" element={<HrDirectory />} />
              <Route path="attendance" element={<Navigate to="/dashboard/employee/my-attendance" replace />} />
              <Route
                path="leave-balances"
                element={<Navigate to="/dashboard/employee/my-attendance" replace />}
              />
              <Route path="organization" element={<HrOrganization />} />
              <Route path="shifts" element={<AllocationShifts />} />
              <Route path="documents" element={<Navigate to="/dashboard/employee/directory" replace />} />
              <Route path="announcements" element={<Navigate to="/dashboard/employee/directory" replace />} />
              <Route path="punch" element={<Navigate to="/dashboard/employee/my-attendance" replace />} />
              <Route path="me" element={<Navigate to="/dashboard/employee/my-attendance" replace />} />
            </Route>
          </Route>
        </Route>

        <Route path="/dashboard/hr" element={<HrLegacyRedirect />} />
        <Route path="/dashboard/hr/*" element={<HrLegacyRedirect />} />

        {/* Smile Design Portal */}
        <Route
          path="/dashboard/smiledesign"
          element={<SmileDashboardLayout />}
        >
          <Route
            element={
              <SmileCheckAuth
                allowedRoles={smilePortalClientRoles}
              />
            }
          >
            <Route path="profile" element={<Profile />} />
            <Route path="generate" element={<SmileGeneration />} />
            <Route path="generate/:smile_id" element={<SmileGeneration />} />
            <Route path="my-smiles" element={<MySmiles />} />
            <Route path="list-credits" element={<ListCreditPackages />} />
            <Route path="my-orders" element={<MyOrders />} />
            <Route path="payment-success" element={<PaymentSuccess />} />
            <Route path="payment-cancelled" element={<PaymentCancelled />} />
          </Route>

          <Route
            element={
              <SmileCheckAuth
                allowedRoles={smilePortalAdminRoles}
              />
            }
          >
            <Route path="all-smiles" element={<AllSmiles />} />
            <Route path="manual-credits" element={<ManualCredits />} />
            <Route path="credit-audit-logs" element={<CreditAuditLogs />} />
            <Route path="credit-usage" element={<CreditUsageHistory />} />
            <Route path="package-manager" element={<PackageManager />} />
            <Route path="payment-manager" element={<PaymentManager />} />
            <Route path="prompt-manager" element={<PromptManager />} />
            <Route path="refund-manager" element={<RefundManager />} />
          </Route>

          <Route
            element={
              <SmileCheckAuth
                allowedRoles={superAdminRoles}
              />
            }
          >
            <Route path="client-management" element={<ClientManagement />} />
          </Route>


        </Route>


        {/* 404 route */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>


      {/* Whatsapp chat option */}
      {!location.pathname.startsWith("/dashboard") && (
        <a
          href="https://wa.me/919310472119"
          target="_blank"
          rel="noopener noreferrer"
          className="w-24 h-24 fixed bottom-4 right-4 z-50 p-2 rounded-full
               animate-[flickerSlide_4s_ease-in-out_infinite]"
        >
          <img
            src="/whatsappLogo.png"
            className="w-full h-full object-cover rounded-full"
            alt="WhatsApp"
          />
        </a>
      )}
    </div>
  );
};

export default App;
